import React from 'react';
import { Vault, Banknote, ArrowDownCircle, Lock, Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function CashDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.caisse.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/tresorerie/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم الصندوق' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات النقد والعمليات اليومية' })}
      icon={Vault}
      accent="from-amber-600 to-orange-800"
      recentTitle={p('recent_transactions', { defaultValue: 'آخر العمليات' })}
      quickLinks={[
        { label: p('link_journal', { defaultValue: 'يومية الصندوق' }), href: '/treasury/journal', icon: Vault },
        { label: p('link_vouchers', { defaultValue: 'السندات' }), href: '/treasury/vouchers', icon: Banknote },
        { label: p('link_main', { defaultValue: 'الصندوق الرئيسي' }), href: '/treasury/main', icon: Lock },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/treasury/analyses-rapports', icon: Vault },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'receipt',
          'income',
          'encaissement',
          'in',
        ]);

        return {
          hero: {
            label: p('kpi_cash_on_hand', { defaultValue: 'النقد المتاح' }),
            value: formatBrandMoney(kpis.cashOnHand || 0),
            icon: Vault,
          },
          kpis: withKpiVariants([
            { label: p('kpi_receipts', { defaultValue: 'مقبوضات اليوم' }), value: formatBrandMoney(kpis.todayReceipts || 0), icon: Banknote },
            { label: p('kpi_expenses', { defaultValue: 'مصروفات اليوم' }), value: formatBrandMoney(kpis.todayExpenses || 0), icon: ArrowDownCircle },
            { label: p('kpi_safe', { defaultValue: 'رصيد الخزنة' }), value: formatBrandMoney(kpis.safeBalance || 0), icon: Lock },
            {
              label: p('kpi_operations_today', { defaultValue: 'عمليات اليوم' }),
              value: sumRowValues(chartRows),
              icon: Activity,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_cashflow', { defaultValue: 'عمليات اليوم' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'مقبوضات ومصروفات وخزنة' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_receipts', { defaultValue: 'مقبوضات اليوم' }), value: Number(kpis.todayReceipts) || 0 },
                { label: p('kpi_expenses', { defaultValue: 'مصروفات اليوم' }), value: Number(kpis.todayExpenses) || 0 },
                { label: p('kpi_safe', { defaultValue: 'رصيد الخزنة' }), value: Number(kpis.safeBalance) || 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'تدفق اليوم' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'مقبوضات مقابل مصروفات' }),
              active: active || Math.round(Number(kpis.todayReceipts) || 0),
              inactive: inactive || Math.round(Number(kpis.todayExpenses) || 0),
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'أنواع العمليات' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'عمليات اليوم حسب النوع' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
